import { Worklist } from "features/WorklistTools/shared/interfaces";
import { useAppReduxSelector } from "hooks/reduxHooks";
import {
  buildBatchedDestProcessingWorklist,
  buildDeadTotalPlateWorklist,
  buildExternalThawTubeWorklist,
  buildInt1ToDestWorklist,
  buildIntCentrifugationWorklist,
  buildIntermediatWellsWorklist,
  buildMappingFileWorklist,
  buildSeedingStrategyWorklist,
} from "../ExternalThawToolHelpers";
import { ExternalThawInternalAction } from "./actions";
import { useExternalThawStateProvider } from "./StateProvider";

export const useHandleMethodSettingsUpdated = () => {
  const { externalThawDispatch } = useExternalThawStateProvider();
  return (key: string, value: any) => {
    externalThawDispatch({
      type: ExternalThawInternalAction.UPDATE_METHOD_SETTINGS,
      payload: {
        key,
        value,
      },
    });
  };
};

export const useSetRequiredWarnings = () => {
  const { externalThawDispatch } = useExternalThawStateProvider();
  return (value: string[]) => {
    externalThawDispatch({
      type: ExternalThawInternalAction.SET_REQUIRED_WARNINGS,
      payload: {
        requiredWarnings: value,
      },
    });
  };
};

export const useSetOptionalWarnings = () => {
  const { externalThawDispatch } = useExternalThawStateProvider();
  return (value: string[]) => {
    externalThawDispatch({
      type: ExternalThawInternalAction.SET_OPTIONAL_WARNINGS,
      payload: {
        optionalWarnings: value,
      },
    });
  };
};

export const useSetWorklist = () => {
  const { externalThawDispatch } = useExternalThawStateProvider();
  return (worklist: any[]) => {
    externalThawDispatch({
      type: ExternalThawInternalAction.SET_WORKLIST,
      payload: {
        worklist: worklist,
        worklistPreviewOpen: true,
      },
    });
  };
};

export const useSetWorkistPreviewOpen = () => {
  const { externalThawDispatch } = useExternalThawStateProvider();
  return (worklistPreviewOpen: boolean) => {
    externalThawDispatch({
      type: ExternalThawInternalAction.SET_WORKLIST_PREVIEW_OPEN,
      payload: {
        worklistPreviewOpen: worklistPreviewOpen,
      },
    });
  };
};

export const useGenerateWorklistClick = () => {
  const { externalThawInternalState, externalThawDispatch } =
    useExternalThawStateProvider();

  const worklistValues = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.worklistValues
  );
  const sourcePlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.sourcePlateInfo
  );
  const destPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.destPlateInfo
  );
  const intPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.intPlateInfo
  );
  const deadPlateBarcode = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.deadPlateBarcode
  );
  const deadPlateType = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.deadPlateType
  );

  return ({ batchDestPlates }: { batchDestPlates: boolean }) => {
    const externalThawTubes = buildExternalThawTubeWorklist(sourcePlateInfo);
    const intermediatePlateMappings = buildIntermediatWellsWorklist(
      worklistValues.harvestWells,
      sourcePlateInfo,
      intPlateInfo
    );
    const deadTotalPlate = buildDeadTotalPlateWorklist(
      externalThawInternalState.methodSettings,
      deadPlateType,
      deadPlateBarcode
    );
    const seedingStrategy = buildSeedingStrategyWorklist(
      externalThawInternalState.methodSettings
    );
    const destinationPlateMappings = buildInt1ToDestWorklist(
      worklistValues.int1ToDest,
      intPlateInfo,
      destPlateInfo
    );
    const batchedDestPlateProcessing =
      buildBatchedDestProcessingWorklist(batchDestPlates);
    const intSpinParams = buildIntCentrifugationWorklist(
      externalThawInternalState.methodSettings
    );
    const mappingFileKeywords = buildMappingFileWorklist(
      sourcePlateInfo,
      intPlateInfo,
      destPlateInfo
    );

    const worklist: Worklist[] = [
      ...externalThawTubes,
      ...intermediatePlateMappings,
      deadTotalPlate,
      seedingStrategy,
      ...destinationPlateMappings,
      batchedDestPlateProcessing,
      intSpinParams,
      ...mappingFileKeywords,
    ];

    externalThawDispatch({
      type: ExternalThawInternalAction.SET_WORKLIST,
      payload: {
        worklist,
        worklistPreviewOpen: true,
      },
    });
  };
};
